import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../../../components/Layout/Layout';
import { isBusinessOrRecipientNameValid, validateFeature } from '../utils';
import {
  CUSTOM_ERROR_TITLE_MAP,
  isRequireDataForVendorValid,
  MoveMoneySteps,
  MoveMoneyTypes,
  StepsToShowProfile,
} from '../consts';
import { VIEWS_MAP } from '../../../router/constants';
import { ISupplierModel } from '../../Supplier/types';
import { StepSwitcher } from '../components/StepSwitcher';
import { ErrorModal } from '../../../generic-components/ErrorModal';
import { useMoveMoneyLogic } from '../hooks/useMoveMoneyLogic';
import { eventBus } from '../../../services/eventBus';
// import { getInternationalWireLimits } from '../api/getInternationalWireLimits';
// import { getInternationalWireFees } from '../api/getInternationalWireFees';

export const MoveMoney = () => {
  const { moveType } = useParams();
  const navigate = useNavigate();
  const {
    getSupplierList,
    fetchMailLimits,
    fetchWireLimits,
    fetchAchOutLimits,
    fetchWireFees,
    fetchMailFees,
    setMoveMoneyData,
    getMoveMoneyData,
    resetMoveMoneyData,
    moveMoneyData,
    wireLimitsError,
    achLimitsError,
    fetchInternationalData,
  } = useMoveMoneyLogic();
  const { t } = useTranslation();
  const [limitsErrMsg, setLimitsErrMsg] = useState<{ title: string; body: string }>({ title: '', body: '' });

  const handleLimitsError = (errTitle: string, errBody: string) => {
    setLimitsErrMsg({ title: errTitle, body: errBody });
    setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.INTRO });
  };

  const getVendors = async (): Promise<number> => {
    const supplierResponse = await getSupplierList();
    const supplierList: ISupplierModel[] = supplierResponse?.data?.suppliers;
    const currMoveMoneyData = getMoveMoneyData();
    setMoveMoneyData({ ...currMoveMoneyData, supplierList });
    return supplierList?.length || 0;
  };

  const handleLimits = async (type?: MoveMoneyTypes) => {
    const checkType = type || moveType;

    if (checkType === MoveMoneyTypes.WIRE_OUT) {
      const limitsResponse = await fetchWireLimits();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, limitsData: limitsResponse.data });
    } else if (checkType === MoveMoneyTypes.MAIL_CHECK) {
      const limitsResponse = await fetchMailLimits();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, limitsData: limitsResponse.data });
    } else if (checkType === MoveMoneyTypes.ACH_OUT_TRANSFER) {
      const limitsResponse = await fetchAchOutLimits();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, limitsData: limitsResponse.data });
    }
  };

  const handleFees = async (type?: MoveMoneyTypes) => {
    const checkType = type || moveType;

    if (checkType === MoveMoneyTypes.WIRE_OUT) {
      const feesResponse = await fetchWireFees();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, feeData: feesResponse.data });
    } else if (checkType === MoveMoneyTypes.MAIL_CHECK) {
      const feesResponse = await fetchMailFees();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, feeData: feesResponse.data });
    }
  };

  const handleCombinedData = async (type?: MoveMoneyTypes) => {
    const checkType = type || moveType;

    if (checkType !== MoveMoneyTypes.INTERNATIONAL_WIRE_OUT) return;
    const { data } = await fetchInternationalData();
    const currMoveMoneyData = getMoveMoneyData();
    setMoveMoneyData({
      ...currMoveMoneyData,
      introRequired: data?.introRequired || false,
      eddRequired: data?.eddRequired || false,
      eddVersion: data?.eddVersion || '',
      purposes: data?.purposes || [],
      limitsData: {
        singleLimit: data?.limits.singleLimit,
        dailyLimit: data?.limits.dailyLimit,
        monthlyLimit: data?.limits.monthlyLimit,
        balance: data?.limits.balance || 0,
      },
      feeData: {
        fees: data?.fees || [],
      },
    });
  };

  const checkIfSupplierAlreadyPopulated = (): boolean => !!getMoveMoneyData().vendorData;

  const checkIfAmountAlreadyPopulated = (): boolean => !!getMoveMoneyData().amount;

  const handleStartFlow = async (type?: MoveMoneyTypes) => {
    if (!validateFeature(type || (moveType as MoveMoneyTypes))) {
      navigate(`/${VIEWS_MAP.DASHBOARD}`);
    }
    setMoveMoneyData({ ...getMoveMoneyData(), isLoadingInitialData: true });

    const vendorsCount = await getVendors();
    await handleCombinedData(type);

    if (getMoveMoneyData().introRequired && !getMoveMoneyData().billId) {
      setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.INTRO, isLoadingInitialData: false });
      return;
    }

    await handleLimits(type);
    await handleFees(type);

    const isSupplierAlreadySet = checkIfSupplierAlreadyPopulated();
    const isAmountAlreadySet = checkIfAmountAlreadyPopulated();

    setMoveMoneyData({ ...getMoveMoneyData(), isLoadingInitialData: false });

    if (isSupplierAlreadySet && isAmountAlreadySet) {
      const isNameValid = isBusinessOrRecipientNameValid(getMoveMoneyData()?.vendorData?.name || '');
      const isMainDataValid = isRequireDataForVendorValid(getMoveMoneyData().vendorData, moveType as MoveMoneyTypes);
      if (!isMainDataValid || !isNameValid) {
        setMoveMoneyData({
          ...getMoveMoneyData(),
          isEditMode: true,
          activeStepId: MoveMoneySteps.VENDOR_UPDATE,
          isVendorUpdateMandatory: !isMainDataValid,
          isUpdateNameRequired: !isNameValid,
        });
      } else if (moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT) {
        setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.PURPOSE_OF_TRANSFER });
      } else {
        setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.REVIEW });
      }
    } else if (isSupplierAlreadySet && !isAmountAlreadySet) {
      setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.AMOUNT });
    } else {
      const { limitsData } = getMoveMoneyData();

      if (!limitsData) {
        setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.INTRO });
        return;
      }

      if (vendorsCount) {
        setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.VENDOR_PICKER });
      } else {
        setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.INTRO });
      }
    }
  };

  const shouldHideProfile = !StepsToShowProfile.includes(moveMoneyData.activeStepId);

  const handleMoveTypeChanged = (type: MoveMoneyTypes) => {
    setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.PRE_LOAD });
    resetMoveMoneyData();
    handleStartFlow(type);
  };

  useEffect(() => {
    const unSubscribe = eventBus.on('moveTypeChanged', (type: MoveMoneyTypes) => handleMoveTypeChanged(type));

    handleStartFlow();

    return () => {
      unSubscribe();
      resetMoveMoneyData();
    };
  }, []);

  useEffect(() => {
    if (wireLimitsError) {
      handleLimitsError('', wireLimitsError.localizedMessage);
    } else if (achLimitsError) {
      handleLimitsError(
        CUSTOM_ERROR_TITLE_MAP[achLimitsError.errCode as keyof typeof CUSTOM_ERROR_TITLE_MAP] || '',
        achLimitsError.errMsg,
      );
    }
  }, [wireLimitsError, achLimitsError]);

  return (
    <Layout
      shouldHideProfile={shouldHideProfile}
      wrapperClassName='pt-[90px] pr-[30px] pl-[50px] bg-bw02 w-full h-full ml-[100px] pc-md:ml-[256px]'
      shouldHideChatBot
    >
      <StepSwitcher />
      {limitsErrMsg.body && (
        <ErrorModal
          customTitle={limitsErrMsg.title}
          onClose={() => navigate(`/${VIEWS_MAP.DASHBOARD}`)}
          onButtonClick={() => navigate(`/${VIEWS_MAP.DASHBOARD}`)}
          text={limitsErrMsg.body || ''}
          buttonText={t('ADD_MONEY_TRANSFER_ERROR_MODAL_CTA')}
        />
      )}
    </Layout>
  );
};
